import type { Ctx } from '../ctx';
import { ago, fmt } from '../model';
import { DialChips, EnvChip, RegistrationChip, StateLine, VerdictChip } from '../ui';
import type { WebhookEntry } from '../types';

export default function WebhooksList({ ctx }: { ctx: Ctx }) {
  const { data, env, entries, pipelineFor, verdict } = ctx;

  const lastFor = (w: WebhookEntry): string | null => {
    const rows = w.pipeline === 'tts' ? data.tts : w.pipeline === 'calendly' ? data.calendly : [];
    if (!rows.length) return null;
    const r = rows[0];
    return 'first_seen' in r ? r.first_seen : r.created_at;
  };

  return (
    <>
      <h1>
        Webhooks <EnvChip env={env} />
      </h1>
      <p className="sub">
        Every webhook registered on the sources this env talks to — ours carry a pipeline in this
        service; external ones (Zapier, other services) are shown for the full picture and can only
        be inspected.
      </p>
      <StateLine loading={data.loading} error={data.error} />
      {data.webhooks && !data.webhooks.siteListed ? (
        <p className="notice" style={{ marginBottom: 14 }}>
          The source site could not be listed — registration state below is what this service knows,
          not what the source reports.
        </p>
      ) : null}

      <section className="card">
        <div className="tw">
          <table>
            <thead>
              <tr>
                <th>Webhook</th>
                <th>Source</th>
                <th>Trigger</th>
                <th>Target</th>
                <th>Registered</th>
                <th>Pipeline</th>
                <th>Monitor</th>
                <th>Last received</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {entries.map((w) => {
                const p = pipelineFor(w);
                const ext = !p;
                const tab = ext ? 'settings' : 'pipeline';
                const open = (): void => ctx.go({ view: 'webhook', id: w.id, tab });
                const last = p ? lastFor(w) : null;
                return (
                  <tr
                    className="click"
                    key={w.id}
                    style={ext ? { opacity: 0.8 } : undefined}
                    onClick={open}
                  >
                    <td>
                      <strong>{p ? p.name : w.owner === 'zapier' ? 'Zapier catch hook' : 'External intake'}</strong>
                      <div className="mono dim" style={{ fontSize: 11 }}>
                        {w.registered === true ? w.id : w.registered === 'unknown' ? 'unknown' : w.registered === 'receiving' ? 'subscription (not listable)' : 'no registration'}
                      </div>
                    </td>
                    <td>{w.source}</td>
                    <td>
                      <code>{w.trigger}</code>
                    </td>
                    <td className="wrap">
                      <code style={{ fontSize: 11.5 }}>{w.target}</code>
                      {w.note ? <div className="note">{w.note}</div> : null}
                    </td>
                    <td>
                      <RegistrationChip entry={w} short />{' '}
                      {w.registered === true && w.createdOn ? <span className="dim">{fmt(w.createdOn).slice(0, 10)}</span> : null}
                    </td>
                    <td>
                      {p ? (
                        <DialChips dial={w.pipeline ? data.dials?.[w.pipeline] : undefined} parked={p.parked} />
                      ) : (
                        <span className="chip">external</span>
                      )}
                    </td>
                    <td>
                      <VerdictChip v={verdict(w)} />
                    </td>
                    <td className="dim">{last ? ago(last) : '—'}</td>
                    <td>
                      <button
                        type="button"
                        className="btn sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          open();
                        }}
                      >
                        Open
                      </button>
                    </td>
                  </tr>
                );
              })}
              {!entries.length ? (
                <tr>
                  <td colSpan={9}>
                    <div className="empty">
                      {data.loading ? 'loading…' : 'No webhooks listed for this env.'}
                    </div>
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      </section>
      <p className="note">
        Register or rotate a source webhook from its pipeline's <strong>Settings</strong> tab (the one-time signing
        secret is shown there for you to store in GCP). A
        brand-new route (another form, OptiSales hand-offs) is a small MR first: route + pipeline +
        ledger table.
      </p>
    </>
  );
}
